import { useState, useEffect } from 'react';
import { MODULES, type MemberPermission, type DataAssignment, type OrganizationMember } from '@/hooks/useOrganization';
import { useFinance } from '@/context/FinanceContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Settings, Database, Loader2, Eye, Plus, Pencil, Trash2 } from 'lucide-react';

interface MemberPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  member?: OrganizationMember;
  permissions: MemberPermission[];
  dataAssignments: DataAssignment[];
  onSavePermissions: (permissions: Partial<MemberPermission>[]) => Promise<boolean>;
  onSaveAssignments: (tableName: string, recordIds: string[]) => Promise<boolean>;
}

type PermissionState = Record<string, {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}>;

export default function MemberPermissionsDialog({
  open,
  onOpenChange,
  memberId,
  member,
  permissions,
  dataAssignments,
  onSavePermissions,
  onSaveAssignments,
}: MemberPermissionsDialogProps) {
  const { accounts, costCenters } = useFinance();
  const [activeTab, setActiveTab] = useState('permissions');
  const [isLoading, setIsLoading] = useState(false);

  // Permission state
  const [permState, setPermState] = useState<PermissionState>({});

  // Data assignment state
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedCostCenters, setSelectedCostCenters] = useState<string[]>([]);

  // Initialize state from props
  useEffect(() => {
    const initial: PermissionState = {};
    MODULES.forEach(m => {
      const existing = permissions.find(p => p.module === m.key);
      initial[m.key] = {
        can_view: existing?.can_view ?? true,
        can_create: existing?.can_create ?? true,
        can_edit: existing?.can_edit ?? true,
        can_delete: existing?.can_delete ?? true,
      };
    });
    setPermState(initial);

    // Data assignments
    const accountAssigns = dataAssignments.filter(a => a.table_name === 'accounts').map(a => a.record_id);
    const ccAssigns = dataAssignments.filter(a => a.table_name === 'cost_centers').map(a => a.record_id);
    setSelectedAccounts(accountAssigns);
    setSelectedCostCenters(ccAssigns);
  }, [permissions, dataAssignments]);

  const handlePermChange = (module: string, perm: keyof PermissionState[string], value: boolean) => {
    setPermState(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [perm]: value,
      },
    }));
  };

  const toggleAllForModule = (module: string, enable: boolean) => {
    setPermState(prev => ({
      ...prev,
      [module]: {
        can_view: enable,
        can_create: enable,
        can_edit: enable,
        can_delete: enable,
      },
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Save permissions
      const permsToSave = Object.entries(permState)
        .map(([module, perms]) => ({
          module,
          ...perms,
        }));
      
      await onSavePermissions(permsToSave);

      // Save data assignments
      await onSaveAssignments('accounts', selectedAccounts);
      await onSaveAssignments('cost_centers', selectedCostCenters);

      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const displayName = member?.profile?.display_name || member?.invited_email || 'Membro';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Permissões: {displayName}
          </DialogTitle>
          <DialogDescription>
            Configure as permissões de acesso para este membro.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="permissions" className="gap-2">
              <Settings className="h-4 w-4" />
              Permissões por Módulo
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2">
              <Database className="h-4 w-4" />
              Atribuição de Dados
            </TabsTrigger>
          </TabsList>

          <TabsContent value="permissions" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {MODULES.map(module => {
                  const state = permState[module.key] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
                  const hasAny = state.can_view || state.can_create || state.can_edit || state.can_delete;
                  const hasAll = state.can_view && state.can_create && state.can_edit && state.can_delete;

                  return (
                    <div key={module.key} className="p-4 rounded-lg border bg-card">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{module.label}</span>
                          {hasAny && (
                            <Badge variant="secondary" className="text-xs">
                              {hasAll ? 'Acesso Total' : 'Parcial'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAllForModule(module.key, true)}
                            className="text-xs"
                          >
                            Todos
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAllForModule(module.key, false)}
                            className="text-xs"
                          >
                            Nenhum
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={state.can_view}
                            onCheckedChange={(checked) => 
                              handlePermChange(module.key, 'can_view', !!checked)
                            }
                          />
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Ver</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={state.can_create}
                            onCheckedChange={(checked) => 
                              handlePermChange(module.key, 'can_create', !!checked)
                            }
                          />
                          <Plus className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Criar</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={state.can_edit}
                            onCheckedChange={(checked) => 
                              handlePermChange(module.key, 'can_edit', !!checked)
                            }
                          />
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Editar</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={state.can_delete}
                            onCheckedChange={(checked) => 
                              handlePermChange(module.key, 'can_delete', !!checked)
                            }
                          />
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Excluir</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="data" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-6">
                {/* Accounts */}
                <div>
                  <h4 className="font-medium mb-3">Contas Bancárias</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Selecione quais contas este membro pode visualizar.
                  </p>
                  <div className="space-y-2">
                    {accounts.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">
                        Nenhuma conta cadastrada
                      </p>
                    ) : (
                      accounts.map(account => (
                        <label
                          key={account.id}
                          className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={selectedAccounts.includes(account.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedAccounts(prev => [...prev, account.id]);
                              } else {
                                setSelectedAccounts(prev => prev.filter(id => id !== account.id));
                              }
                            }}
                          />
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: account.color || '#64748b' }}
                          />
                          <span>{account.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Cost Centers */}
                <div>
                  <h4 className="font-medium mb-3">Centros de Custo</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Selecione quais centros de custo este membro pode visualizar.
                  </p>
                  <div className="space-y-2">
                    {costCenters.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">
                        Nenhum centro de custo cadastrado
                      </p>
                    ) : (
                      costCenters.map(cc => (
                        <label
                          key={cc.id}
                          className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={selectedCostCenters.includes(cc.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCostCenters(prev => [...prev, cc.id]);
                              } else {
                                setSelectedCostCenters(prev => prev.filter(id => id !== cc.id));
                              }
                            }}
                          />
                          <span>{cc.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Permissões'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
